import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ownerApi } from '../../lib/ownerApi';
import { ArrowLeft, Mail, Building2, ShieldAlert, Activity } from 'lucide-react';

function Section({ title, icon: Icon, children, testId }) {
  return (
    <section className="rounded-2xl border border orka-border-subtle orka-surface-raised p-4" data-testid={testId}>
      <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4 text-stone-500" />}
        {title}
      </h3>
      {children}
    </section>
  );
}

function KV({ k, v, mono }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 border-b border-stone-100 dark:border-slate-800 last:border-0">
      <span className="text-[11px] uppercase tracking-wider text-stone-500">{k}</span>
      <span className={`text-xs ${mono ? 'font-mono orka-text-secondary' : 'orka-text-primary'} truncate max-w-[60%]`}>
        {v == null || v === '' ? <span className="text-stone-400 italic">—</span> : String(v)}
      </span>
    </div>
  );
}

export default function OwnerPersonDetailPage() {
  const { person_id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    ownerApi.getPerson(person_id)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [person_id]);

  if (loading || !data) {
    return <div className="text-stone-500 text-sm" data-testid="owner-person-loading">Loading…</div>;
  }

  const p = data.person || {};
  const u = data.user || {};
  const account = data.commercial_account;

  return (
    <div className="space-y-4" data-testid="owner-person-detail">
      <Link to="/owner/people" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to People
      </Link>

      <div className="rounded-2xl border border orka-border-subtle orka-surface-raised p-5">
        <h1 className="text-xl font-semibold">{p.display_name || u.name || p.primary_email || person_id}</h1>
        <div className="text-xs text-stone-500 font-mono mt-1">{p.person_id || person_id}</div>
        {account && (
          <Link
            to={`/owner/accounts/${encodeURIComponent(account.account_id)}`}
            className="mt-3 inline-block text-xs text-violet-600 dark:text-violet-400 hover:underline"
            data-testid="owner-person-account-link"
          >
            → Open commercial account ({account.plan_key})
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Person record" icon={Mail} testId="owner-section-person">
          <KV k="Display name" v={p.display_name} />
          <KV k="Primary email" v={p.primary_email} />
          <KV k="Person ID" v={p.person_id} mono />
          <KV k="Created" v={p.created_at?.slice(0, 10)} />
          <KV k="Updated" v={p.updated_at?.slice(0, 10)} />
        </Section>

        <Section title="Auth (User)" icon={ShieldAlert} testId="owner-section-user">
          <KV k="Email" v={u.email} />
          <KV k="User ID" v={u.user_id} mono />
          <KV k="Has authenticated" v={u.has_authenticated ? 'yes' : 'no'} />
          <KV k="Last active" v={u.last_active_at?.slice(0, 10)} />
        </Section>

        <Section title="Memberships" icon={Building2} testId="owner-section-memberships">
          {(data.memberships || []).length === 0 ? (
            <div className="text-xs text-stone-400 italic">No memberships.</div>
          ) : (
            <ul className="space-y-1">
              {data.memberships.map(m => (
                <li key={m.membership_id} className="text-xs flex items-center gap-2 border-b border-stone-100 dark:border-slate-800 py-1.5 last:border-0">
                  <span className="font-mono text-stone-500">{m.org_id}</span>
                  <span>{m.role}</span>
                  <span className={`ml-auto px-1.5 py-0.5 rounded text-[10px] ${m.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-600'}`}>{m.status}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Sessions" icon={Activity} testId="owner-section-sessions">
          {(data.sessions || []).length === 0 ? (
            <div className="text-xs text-stone-400 italic">No active sessions.</div>
          ) : (
            <ul className="space-y-1">
              {data.sessions.slice(0, 10).map(s => (
                <li key={s.session_id || s.created_at} className="text-xs flex items-center gap-2 border-b border-stone-100 dark:border-slate-800 py-1.5 last:border-0">
                  <span className="font-mono text-stone-500 shrink-0">{(s.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                  <span className="orka-text-secondary truncate">{s.user_agent || '—'}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      <Section title="Recent owner actions" icon={Activity} testId="owner-section-events">
        {(data.events || []).length === 0 ? (
          <div className="text-xs text-stone-400 italic">No owner actions yet.</div>
        ) : (
          <ul className="space-y-1.5">
            {data.events.map(e => (
              <li key={e.event_id} className="text-xs flex items-center gap-2 border-b border-stone-100 dark:border-slate-800 py-1.5 last:border-0">
                <span className="font-mono text-stone-400">{(e.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                <span className="font-medium">{e.event_type}</span>
                <span className="text-stone-500">{e.actor_email}</span>
                {e.reason && <span className="italic text-stone-600">— {e.reason}</span>}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
